import React, { useContext } from 'react';
import { Badge, Tag, theme, Tooltip } from 'antd';
import { WhatsAppOutlined } from '@ant-design/icons';
import { ThemeContext } from '../../../../ThemeContext';
import { WindowState, formatRemaining } from '../../../../utils/whatsappWindow';
import { channelColor } from '../../../common/ChannelIcon';

export const WHATSAPP_WINDOW_TOOLTIP =
  "WhatsApp allows free-form replies for 24 hours after the candidate's last message. "
  + 'After that, only an approved template or SMS can reach them.';

const LIGHT_OPEN_STYLE: React.CSSProperties = { background: '#e6f7e9', borderColor: '#95e09e' };

export interface WhatsAppWindowChipProps {
  state: WindowState;
  /** Icon + remaining time only (thread list rows). */
  compact?: boolean;
  onClick?: () => void;
  /** Show the live "conversation active" pulse when the window is open. */
  hasConversation?: boolean;
}

const chipText = (state: WindowState, compact: boolean): string => {
  const remaining = formatRemaining(state.remainingMs);
  switch (state.level) {
    case 'open':
    case 'closing':
      return compact ? remaining : `WhatsApp · ${remaining} left`;
    case 'closed':
      return compact ? 'closed' : 'WhatsApp · window closed';
    default:
      return compact ? '—' : 'WhatsApp · no conversation yet';
  }
};

/**
 * Status pill for the WhatsApp 24h reply window (ADR-025).
 * Levels: open (green), closing (<1h, amber), closed / none (grey).
 */
const WhatsAppWindowChip: React.FC<WhatsAppWindowChipProps> = ({
  state,
  compact = false,
  onClick,
  hasConversation = false,
}) => {
  const { token } = theme.useToken();
  // useContext (not useTheme) so the chip also renders outside ThemeProvider.
  const themeCtx = useContext(ThemeContext) as { isDarkMode?: boolean } | undefined;
  const isDark = !!themeCtx?.isDarkMode;

  const isOpen = state.level === 'open';
  const isClosing = state.level === 'closing';
  const whatsappGreen = channelColor('whatsapp');

  let openStyle: React.CSSProperties = {};
  if (isOpen) {
    openStyle = isDark
      ? { background: token.colorPrimaryBg, borderColor: token.colorPrimaryBorder }
      : LIGHT_OPEN_STYLE;
  }

  const style: React.CSSProperties = {
    margin: 0,
    cursor: onClick ? 'pointer' : 'default',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    ...openStyle,
  };

  return (
    <Tooltip title={WHATSAPP_WINDOW_TOOLTIP}>
      <Tag
        data-testid="whatsapp-window-chip"
        data-level={state.level}
        color={isClosing ? 'warning' : undefined}
        onClick={onClick}
        style={style}
        icon={<WhatsAppOutlined style={{ color: isOpen || isClosing ? whatsappGreen : undefined }} />}
      >
        {isOpen && hasConversation && (
          <Badge status="processing" color={whatsappGreen} data-testid="whatsapp-window-pulse" />
        )}
        <span>{chipText(state, compact)}</span>
      </Tag>
    </Tooltip>
  );
};

export default WhatsAppWindowChip;
